import { computed, Injectable } from '@angular/core';
import { UserService } from './auth/user.service';
import { ConfigService, getConfigOrElse } from './config/config.service';
import { LoggerFactory } from './logger/logger-factory.service';
import { LoggerWrapper } from './logger/logger-wrapper.service';
import { SitesFavorisProvider } from './sites-favoris/sites-favoris.provider';
import { GetInitService } from './synchronisation/get-init.service';
import { datadogLogs } from '@datadog/browser-logs';
import { environment } from '../../../environments/environment';
import semver from 'semver/preload';
import { AlertController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { UserGeolocConsentService } from './auth/user-geoloc-consent.service';
import { UserContactControllerApi } from '../../api/services/user-contact-controller-api';
import { GetUserContactByUserId$Params } from '../../api/fn/user-contact-controller/get-user-contact-by-user-id';
import { firstValueFrom } from 'rxjs';
import { GetUserContactResponseDto } from '../../api/models/get-user-contact-response-dto';
import { StorageProvider } from './storage/storage';
import { PortailSecuriteProvider } from './securite/portail-securite.provider';
import { isToggleTopInterventionEnabled } from '../utils/toggle-utils';

@Injectable({
  providedIn: 'root',
})
export class StartupService {
  private logger: LoggerWrapper;
  isGetInitRunning = computed(() => {
    return this.getInitService.isRunning();
  });

  constructor(
    private loggerFactory: LoggerFactory,
    private getInitService: GetInitService,
    private userService: UserService,
    private configService: ConfigService,
    private siteFavorisSerrvice: SitesFavorisProvider,
    private alertController: AlertController,
    private userGeolocConsentService: UserGeolocConsentService,
    private userContactApi: UserContactControllerApi,
    private storageProvider: StorageProvider,
    private portailSecuriteProvider: PortailSecuriteProvider
  ) {
    this.logger = this.loggerFactory.getInstance(this);
  }

  async loadConfigAndUserData() {
    let userId = this.userService.getId();
    await this.configService.syncConfig(userId);
    this.userGeolocConsentService.loadConsent(userId);
    await this.validateAppVersion();
    await this.loadUserContact();
  }

  async loadUserContact() {
    let userId = this.userService.getId();
    let params: GetUserContactByUserId$Params = {
      user_id: userId,
    };
    let getUserContactResponseDto: GetUserContactResponseDto = await firstValueFrom(
      this.userContactApi.getUserContactByUserId(params)
    );
    await this.storageProvider.set('user_contact_' + userId, getUserContactResponseDto.emails, false);
  }

  /**
   *
   * @param delay nombre de secondes avant la synchro
   */
  async synchroDonnees(delay: number = 3) {
    this.logger.info('Lancement de la synchro des données');
    this.siteFavorisSerrvice.searchSitesFavoris();
    if (isToggleTopInterventionEnabled()) {
      this.portailSecuriteProvider.loadDonneesTopIntervention();
    }
    if (delay > 0) {
      setTimeout(() => {
        this.getInitService.launchGetInit();
      }, delay * 1000);
    } else {
      this.getInitService.launchGetInit();
    }
  }

  configureDatadogForUser() {
    const userInfo = this.userService.getCurrentUser();
    datadogLogs.setUser({
      id: userInfo.id,
      email: userInfo.email,
      name: userInfo.matricule,
    });
  }

  async validateAppVersion() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const minVersion: string = getConfigOrElse('VERSION_APP_MINIMALE_REQUISE', '0.0.0');
    const appVersion: string = environment.appVersion;
    console.log('minVersion', minVersion);
    console.log('appVersion', appVersion);
    const obsolete: boolean = semver.lt(appVersion, minVersion);
    if (obsolete) {
      const modal: HTMLIonAlertElement = await this.alertController.create({
        header: 'Version obsolète',
        message:
          'Votre version de D@cTech est obsolète, veuillez procéder à la mise à jour afin de pouvoir continuer à l’utiliser',
        backdropDismiss: false,
        buttons: [
          {
            text: 'Mettre à jour',
            handler: () => {
              window.open('https://play.google.com/store/apps/details?id=' + environment.appId, '_system');
              return false;
            },
          },
        ],
      });
      await modal.present();
    }
  }
}
